import { IntraDayProfileTimeElement } from 'communication/types/intraDayProfile/IntraDayProfileElement';
import { convertToMilliseconds } from 'utils/components/TimeSeriesChart/timeSeriesTimeScale';
import {
    TimeSeriesDisableRange,
    TimeSeriesDisableRangeLabel,
    mapFromElementRangeArray,
} from 'utils/timeseries/TimeSeriesDisableRange';
import {
    computeTimeLabels,
    SessionLabelledTimeSeriesItem,
} from 'utils/components/TimeSeriesChart/timeline';
import { computeTimeSeriesBarWidthPercentage } from 'utils/d3fc/chartSeries';
import omitUndefined from 'utils/omitUndefined';
import {
    IntraDayProfileData,
    IntraDayProfileDataItem,
} from './IntraDayProfileData';
import { IntraDayProfileDataState } from '../store/data/IntraDayProfileStreamDataState';
import {
    MarketDataBarItem,
    MarketDataRefData,
} from 'marketData/store/MarketDataState';

const computeDataSeries: (
    sortedTimeSeries: IntraDayProfileTimeElement[],
    breaksInTrading: TimeSeriesDisableRange[],
    bucketSize: number,
    dapiBarData: undefined | MarketDataBarItem[],
    dapiRefData: undefined | MarketDataRefData
) => IntraDayProfileDataItem[] = (
    sortedTimeSeries,
    breaksInTrading,
    bucketSize,
    dapiBarData,
    dapiRefData
) => {
    const shiftWidth = bucketSize / 2;
    const result: IntraDayProfileDataItem[] = sortedTimeSeries
        .map((d, i, arr) =>
            omitUndefined({
                time: d._i,
                bucketStartTime: convertToMilliseconds(d._i),
                actualTradedVolume: dapiBarData ? undefined : d.prtv,
                historicalTradedVolume: d.p,
                historicalAuctionAndTradedVolume: d.pav,
                historicalCumulativeTradedVolume: d.cp,
                historicalSpread: d.spr,
                historicalVolatility: d.vt,
                auctionVolume: d.pav ? d.pav - (d.p ? d.p : 0) : undefined,
                barWidthPercentage: !(
                    d.si !== undefined && Object.keys(d).length === 2
                )
                    ? computeTimeSeriesBarWidthPercentage(
                          i,
                          arr,
                          bucketSize,
                          breaksInTrading
                      )
                    : undefined,
            })
        )
        .map((d) => ({
            ...d,
            time: computeDataPointTime(d, shiftWidth),
            bucketEndTime: computeDataPointTime(d, bucketSize),
        }))
        .filter((d) => Object.keys(d).length > 3);

    if (dapiBarData && dapiRefData?.averageVolume) {
        enrichWithExternalMarketData(
            result,
            dapiBarData,
            dapiRefData.averageVolume,
            bucketSize
        );
    }

    const now = new Date().getTime();
    let runningTotal = 0;
    for (let x = 0; x < result.length; ++x) {
        const val = result[x];

        if (val.actualTradedVolume) {
            val.cumulativeTradedVolume = runningTotal + val.actualTradedVolume!;
            runningTotal = val.cumulativeTradedVolume;
        } else if (val.bucketStartTime <= now && val.bucketEndTime >= now) {
            val.cumulativeTradedVolume = runningTotal;
        }
    }

    return result;
};

function enrichWithExternalMarketData(
    buckets: IntraDayProfileDataItem[],
    dapiBarData: MarketDataBarItem[],
    averageVolume: number,
    bucketSize: number
) {
    for (const external of dapiBarData) {
        const bucket = buckets.find(
            (b) =>
                b.bucketStartTime <= external.time &&
                b.bucketEndTime > external.time
        );
        const actualTradedVolume =
            ((external.volume ?? 0) / averageVolume) * 100;
        if (bucket) {
            bucket.actualTradedVolume = actualTradedVolume;
        } else {
            const addBucket: IntraDayProfileDataItem = {
                bucketStartTime: external.time,
                bucketEndTime: external.time + bucketSize * 60000,
                time: external.time + bucketSize * 30000,
                actualTradedVolume,
            };
            buckets.push(addBucket);
        }
    }
}

const computeBreaks: (
    excRanges: number[] | undefined
) => TimeSeriesDisableRange[] = (excRanges) =>
    excRanges ? mapFromElementRangeArray(excRanges) : [];

const computeRangeLabels: (
    sortedTimeSeries: IntraDayProfileTimeElement[]
) => TimeSeriesDisableRangeLabel[] = (sortedTimeSeries) => {
    const labelled = sortedTimeSeries.filter(
        (val, index, arr) =>
            index !== 0 && index !== arr.length - 1 && val.si !== undefined
    );

    const res: TimeSeriesDisableRangeLabel[] = [];
    for (let x = 0; x < labelled.length - 1; x += 2) {
        const closeTime = labelled[x];
        const openTime = labelled[x + 1];

        res.push({
            open: convertToMilliseconds(openTime._i),
            openLabel: openTime.si!,
            close: convertToMilliseconds(closeTime._i),
            closeLabel: closeTime.si!,
        });
    }
    return res;
};

export const computeChartData: (
    dataState: IntraDayProfileDataState | undefined,
    bucketSize: number,
    dapiBarData: undefined | MarketDataBarItem[],
    dapiRefData: undefined | MarketDataRefData
) => IntraDayProfileData = (
    dataState,
    bucketSize,
    dapiBarData,
    dapiRefData
) => {
    if (!dataState) {
        return {
            dataSeries: [],
            breaksInTrading: [],
            rangeLabels: [],
            timeLineLabels: [],
        };
    }

    const breaksInTrading = computeBreaks(dataState.excRanges);

    const sortedTimeSeries = dataState.timeSeries.sort((a, b) => a._i - b._i);
    return {
        dataSeries: computeDataSeries(
            sortedTimeSeries,
            breaksInTrading,
            bucketSize,
            dapiBarData,
            dapiRefData
        ),
        breaksInTrading,
        rangeLabels: computeRangeLabels(sortedTimeSeries),
        timeLineLabels: computeTimeLabels(
            sortedTimeSeries
                .filter((val) => val.si !== undefined)
                .map((x) => x as SessionLabelledTimeSeriesItem),
            breaksInTrading,
            convertToMilliseconds(bucketSize / 2)
        ),
    };
};

function computeDataPointTime(
    item: { time: number; barWidthPercentage?: number },
    maxAddWidth: number
) {
    return convertToMilliseconds(
        item.time +
            (item.barWidthPercentage !== undefined
                ? item.barWidthPercentage
                : 1) *
                maxAddWidth
    );
}
